// Global Zustand store for the "Add to Portfolio" overlay.
//
// Why Zustand (and not React Context / prop-drilling): the modal is a GLOBAL
// overlay that any Info Page (stock / etf / reit / crypto) can trigger. Zustand
// lives OUTSIDE the React tree, so opening the modal re-renders ONLY the
// components that subscribe to the changed slice (the modal itself) — the heavy
// charts and tables on the info page underneath stay completely untouched. It
// also needs zero <Provider> wrappers and costs ~1kb.
//
// Usage:
//   const openModal = usePortfolioStore((s) => s.openModal);
//   openModal({ symbol, name, type: 'STOCK', currentPrice });
//
// Subscribe to individual slices (not the whole store) so a component only
// re-renders when the slice it reads actually changes.

import { create } from 'zustand';

export type InstrumentType = 'STOCK' | 'ETF' | 'REIT' | 'CRYPTO';

// The three ways the shared modal can be opened:
//   BUY  — default; the info-page "Add to Portfolio" flow (keeps its own
//          Buy/Sell toggle so nothing about that flow changes).
//   SELL — pre-filled Sell of an existing position (from the ledger row).
//   EDIT — overwrite/reset an existing position to a fresh BUY baseline.
export type ModalMode = 'BUY' | 'SELL' | 'EDIT';

export interface ModalInstrumentData {
    symbol: string;
    name: string;
    type: InstrumentType;
    currentPrice: number;
    // Asset sector/category captured from the triggering Info Page so it can be
    // persisted with the trade and power the dashboard's sector pie chart.
    // Optional (crypto has none; the backend buckets it as "Cryptocurrencies").
    sector?: string | null;
    // Present when the modal is opened from a ledger row (SELL / EDIT) so the
    // form can pre-fill the existing share count.
    currentQuantity?: number;
    // Listing venue + already-resolved listing currency captured from the
    // triggering Info Page, used to pre-select the trade's native currency
    // (e.g. a `.DE` / XETRA asset defaults to EUR). Both optional so existing
    // callers keep working; the modal falls back to the symbol heuristic.
    exchange?: string | null;
    currency?: string | null;
}

interface PortfolioStore {
    isModalOpen: boolean;
    modalMode: ModalMode;
    modalData: ModalInstrumentData | null;
    openModal: (data: ModalInstrumentData, mode?: ModalMode) => void;
    closeModal: () => void;
}

export const usePortfolioStore = create<PortfolioStore>((set) => ({
    isModalOpen: false,
    modalMode: 'BUY',
    modalData: null,
    // Default to BUY so the existing Info Page triggers keep working unchanged.
    openModal: (data, mode = 'BUY') =>
        set({ isModalOpen: true, modalData: data, modalMode: mode }),
    closeModal: () =>
        set({ isModalOpen: false, modalData: null, modalMode: 'BUY' }),
}));
